<template>
  <div class="relative">
    <div
      v-if="showRawMessages"
      class="mb-2"
    >
      <div class="rounded-lg bg-gray-900 p-4">
        <VueJsonPretty
          :data="message"
          :deep="2"
          :show-double-quotes="false"
          :show-length="true"
          class="!bg-transparent"
          :showLine="false"
          :collapsed-on-click-brackets="true"
          :path-selectable="true"
        />
      </div>
    </div>
    <div class="flex gap-x-2 sm:gap-x-4">
      <div class="w-full max-w-[90%] grow space-y-3 md:max-w-2xl">
        <div
          class="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
        >
          <div
            ref="proseContent"
            class="prose prose-sm max-w-none overflow-auto text-sm text-gray-800 dark:prose-invert prose-p:my-1
              prose-pre:border prose-pre:border-neutral-200 prose-pre:bg-neutral-100 prose-ol:my-0.5
              prose-ol:list-decimal prose-ol:pl-5 prose-ul:my-0.5 prose-ul:list-disc prose-ul:pl-5 prose-li:my-0.5
              prose-li:pl-2 prose-li:marker:text-gray-400 dark:text-white dark:prose-pre:border-neutral-700
              dark:prose-pre:bg-neutral-800 dark:prose-li:marker:text-gray-500 [&_a:hover]:text-purple-700
              [&_a]:text-purple-600 [&_a]:no-underline"
            v-html="renderedContent"
          />
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="flex gap-2">
              <button
                :class="[
                  'rounded-md p-1.5 transition-colors',
                  rating === 'positive' && rating !== null
                    ? 'bg-purple-100 text-purple-600'
                    : 'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
                ]"
                :title="rating === 'positive' ? t('MarkedHelpful') : t('Helpful')"
                @click="handleRating('positive')"
              >
                <HandThumbUpIcon class="h-5 w-5" />
              </button>
              <button
                :class="[
                  'rounded-md p-1.5 transition-colors',
                  rating === 'negative'
                    ? 'bg-purple-100 text-purple-600'
                    : 'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
                ]"
                :title="rating === 'negative' ? t('MarkedNotHelpful') : t('NotHelpful')"
                @click="handleRating('negative')"
              >
                <HandThumbDownIcon class="h-5 w-5" />
              </button>
            </div>
          </div>
          <button
            :class="[
              'rounded-md p-1.5 transition-colors',
              'text-gray-400 hover:bg-purple-50 hover:text-purple-600'
            ]"
            :title="copyButtonTitle"
            @click="copyToClipboard"
          >
            <div class="flex items-center gap-1">
              <transition
                name="fade"
                mode="out-in"
              >
                <DocumentDuplicateIcon
                  v-if="!copied"
                  key="copy-icon"
                  class="h-5 w-5"
                />
                <CheckIcon
                  v-else
                  key="check-icon"
                  class="h-5 w-5 text-purple-600"
                />
              </transition>
              <span class="text-sm text-gray-500">Kopieren</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, onMounted, inject, watch, nextTick, onUnmounted, type Ref } from 'vue'
  import { type AIChatSession, type AIChatSessionMessage, FeatureEnum } from '@/types/generated'
  import { FeedbackService } from '@/types/generated/services.gen'
  import IconRenderer from './IconRenderer.vue'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import { createApp, h } from 'vue'
  import MarkdownIt from 'markdown-it'
  import { useTranslation } from '@/composables/useTranslation'
  import { useEntityCard } from '@/composables/useEntityCard'
  import {
    HandThumbUpIcon,
    HandThumbDownIcon,
    DocumentDuplicateIcon,
    CheckIcon
  } from '@heroicons/vue/24/outline'

  const props = defineProps<{
    message: AIChatSessionMessage
  }>()

  const { t } = useTranslation({
    en: {
      Page: 'Page',
      Pages: 'Pages',
      CopyToClipboard: 'Copy to clipboard',
      Copied: 'Copied!',
      Helpful: 'Mark as helpful',
      NotHelpful: 'Mark as not helpful',
      MarkedHelpful: 'Marked as helpful',
      MarkedNotHelpful: 'Marked as not helpful'
    },
    de: {
      Page: 'Seite',
      Pages: 'Seiten',
      CopyToClipboard: 'In die Zwischenablage kopieren',
      Copied: 'Kopiert!',
      Helpful: 'Als hilfreich markieren',
      NotHelpful: 'Als nicht hilfreich markieren',
      MarkedHelpful: 'Als hilfreich markiert',
      MarkedNotHelpful: 'Als nicht hilfreich markiert'
    }
  })

  const { showCard } = useEntityCard()
  const rating = ref<'positive' | 'negative' | null>(null)
  const debugPanelRef = inject('debugPanelRef')
  const showRawMessages = computed(() => debugPanelRef?.value?.showRawMessages || false)

  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  const entityRegistry = new Map()
  const documentRegistry = new Map()
  let registryCounter = 0

  const handleEntityClick = (entity) => {
    showCard(entity, event.target)
    window.dispatchEvent(new CustomEvent('entity-click', { detail: entity }))
  }

  const emit = defineEmits(['document-click'])

  const handleDocumentClick = (document) => {
    emit('document-click', document)
  }

  const renderIcon = (type: 'user' | 'building' | 'book') => {
    const div = document.createElement('div')
    const app = createApp(IconRenderer, { type })
    app.mount(div)
    return div.innerHTML
  }

  const getEntityHtml = (entity) => {
    const iconHtml = renderIcon(entity.is_company ? 'building' : 'user')
    const key = `entity-${registryCounter++}`
    entityRegistry.set(key, entity)
    return `<span class="entity-link inline-flex cursor-pointer items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">${iconHtml}${entity.name}</span>`
  }

  const getDocumentHtml = (doc) => {
    const iconHtml = renderIcon('book')
    const key = `doc-${registryCounter++}`
    documentRegistry.set(key, doc)
    const pageText =
      doc.pages.length === 0
        ? ''
        : doc.pages.length === 1
          ? `${t('Page')} ${doc.pages[0]}`
          : `${t('Pages')}: ${doc.pages.join(', ')}`
    return `<span class="document-link inline-flex cursor-pointer items-center gap-x-1 rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white" data-ref="${key}">
      ${iconHtml}${doc.title}${pageText ? ` <span class="text-gray-500">(${pageText})</span>` : ''}
    </span>`
  }

  const copied = ref(false)
  const copyButtonTitle = computed(() => (copied.value ? t('Copied') : t('CopyToClipboard')))

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))

  const rate = async (value: 'positive' | 'negative') => {
    try {
      await FeedbackService.feedbackCreate({
        requestBody: {
          rating: value === 'positive' ? '5.0' : '0.0',
          feature: FeatureEnum.AI_CHAT,
          url: window.location.href,
          related_data: {
            ai_chat: {
              ai_chat_session_uuid: activeChatSession.value?.uuid,
              ai_chat_session_message_uuid: props.message.uuid
            }
          }
        }
      })
    } catch (error) {
      console.error('Failed to submit rating:', error)
    }
  }

  const handleRating = (value: 'positive' | 'negative') => {
    rate(value)
  }

  const copyToClipboard = async () => {
    const textContent =
      props.message.content?.chunks?.reduce((acc, chunk) => {
        if (chunk.type === 'text') {
          return acc + chunk.text
        } else if (chunk.type === 'entity') {
          return acc + chunk.name
        } else if (chunk.type === 'document') {
          return (
            acc +
            `${chunk.title} (${chunk.pages.length === 1 ? t('Page') + ' ' + chunk.pages[0] : t('Pages') + ': ' + chunk.pages.join(', ')})`
          )
        }
        return acc
      }, '') || ''

    try {
      await navigator.clipboard.writeText(textContent)
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 2000) // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  const renderedContent = computed(() => {
    entityRegistry.clear()
    documentRegistry.clear()
    registryCounter = 0

    if (!props.message.content?.chunks) return ''

    const content = props.message.content.chunks.reduce((acc, chunk) => {
      if (chunk.type === 'text') {
        return acc + chunk.text
      } else if (chunk.type === 'entity') {
        return acc + getEntityHtml(chunk)
      } else if (chunk.type === 'document') {
        return acc + getDocumentHtml(chunk)
      }
      return acc
    }, '')

    return md.render(content)
  })

  const proseContent = ref<HTMLElement | null>(null)

  watch(
    () => props.message.content?.chunks,
    () => {
      nextTick(() => {
        if (proseContent.value) {
          proseContent.value.removeEventListener('click', handleContainerClick)
          proseContent.value.addEventListener('click', handleContainerClick)
        }
      })
    },
    { deep: true }
  )

  const handleContainerClick = (e: Event) => {
    const target = e.target as HTMLElement
    const entityLink = target.closest('.entity-link')
    const documentLink = target.closest('.document-link')

    if (entityLink) {
      const ref = entityLink.getAttribute('data-ref')
      if (ref) {
        const entity = entityRegistry.get(ref)
        if (entity) {
          handleEntityClick(entity)
        }
      }
    }

    if (documentLink) {
      const ref = documentLink.getAttribute('data-ref')
      if (ref) {
        const doc = documentRegistry.get(ref)
        if (doc) {
          emit('document-click', doc)
        }
      }
    }
  }

  onMounted(() => {
    if (proseContent.value) {
      proseContent.value.addEventListener('click', handleContainerClick)
    }
  })

  onUnmounted(() => {
    if (proseContent.value) {
      proseContent.value.removeEventListener('click', handleContainerClick)
    }
  })
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: scale(1);
  }
</style>

<style>
  .prose :deep(p) {
    margin: 0;
    display: inline;
  }

  .prose :deep(ul),
  .prose :deep(ol) {
    margin: 0;
    padding-left: 1.5em;
  }

  .prose :deep(li) {
    margin: 0;
  }

  .prose :deep(li:not(:last-child)) {
    margin-bottom: 0.25em;
  }
</style>

<style scoped>
  :deep(.vjs-tree) {
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
      monospace !important;
    font-size: 0.75rem !important;
    line-height: 1rem !important;
    background-color: transparent !important;
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__string) {
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__number) {
    color: rgb(129 140 248) !important;
  }

  :deep(.vjs-tree .vjs-value__boolean) {
    color: rgb(244 114 182) !important;
  }

  :deep(.vjs-tree .vjs-value__null) {
    color: rgb(248 113 113) !important;
  }

  :deep(.vjs-tree .vjs-key) {
    color: rgb(139 92 246) !important;
  }
</style>
